'use client';

import type { UseInfiniteQueryResult } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import React from 'react';

import { Image } from '@zoomit/primitives';
import { Flex, Link } from '@zoomit/primitives';
import { cssVar, theme, Typography } from '@zoomit/zoomit-ds';

import { OriginURL } from '../../constants/apis';
import { generateTypeIcon } from '../../helpers/notification/generateTypeIcon';
import { useReadNotification } from '../../hooks/notification';
import { useWebKitCustomization } from '../../hooks/useWebKitCustomization';
import type { GetNotificationResponse } from '../../types/notification';
import SomethingWentWrong from '../empty-state/SomethingWentWrong';
import { RelativeTime } from '../shared';
import NotificationSkeleton from './NotificationSkeleton';

type Props = {
  notificationQuery: UseInfiniteQueryResult<AxiosResponse<GetNotificationResponse>, unknown>;
  isCountLoading: boolean;
  onClose: () => void;
};

const Notifications = (props: Props) => {
  const { notificationQuery, isCountLoading, onClose } = props;
  const { data: notifications, isLoading: isNotificationsLoading, isError } = notificationQuery;
  const { mutate: readNotification } = useReadNotification();

  if (isError) return <SomethingWentWrong />;

  if (isCountLoading || isNotificationsLoading || !notifications) {
    return (
      <>
        {Array(10)
          .fill(0)
          .map(() => (
            <NotificationSkeleton />
          ))}
      </>
    );
  }
  const notificationsFlat = notifications.pages.flatMap((item) => item.data.source);

  if (notificationsFlat.length === 0) {
    return <NotificationsEmptyState />;
  }

  return (
    <div className="overflow-auto">
      {notificationsFlat.map(({ id, text, occuredAt, type, link }) => {
        return (
          <Link
            href={`${OriginURL}/${link?.url}`}
            style={{ textDecoration: 'none' }}
            onClick={() => {
              !!link?.url && onClose();
              readNotification(id);
            }}
          >
            <Flex
              key={id}
              spacing={{ py: 'x4' }}
              className={'border-b-grey6 cursor-pointer border-b'}
              flexDirection="column"
              gap={2}
            >
              <Flex justifyContent="space-between">
                <Flex gap={0.6} alignItems="center">
                  {generateTypeIcon(type)}
                  <Typography className="fa" variant="subhead" textColor={cssVar(theme.grey1)}>
                    <RelativeTime time={occuredAt} timeAgoBasis="one-month" />
                  </Typography>
                </Flex>
              </Flex>
              <Typography variant="callout" lineHeight={2.4}>
                {text}
              </Typography>
            </Flex>
          </Link>
        );
      })}
    </div>
  );
};

export default React.memo(Notifications);

function NotificationsEmptyState() {
  const { noNotification } = useWebKitCustomization((x) => x.emptyStatesPublicPath);
  return (
    <div className="flex grow flex-col items-center justify-center gap-4 py-4">
      {noNotification && (
        <Image
          width={noNotification.width || 203}
          height={noNotification.height || 208}
          src={noNotification.publicSrc}
          alt="empty item"
        />
      )}
      <div>هیچ اعلانیه ای وجود ندارد!</div>
    </div>
  );
}
